/**
 * Phase 14.5 +1 — QorBOM Docs onboarding tour.
 *
 * Four-step floating overlay that walks first-time visitors through
 * the new 3-column docs shell:
 *
 *   1. "Here's the left rail" — pin to LeftRail
 *   2. "Code samples in 4 languages" — pin to the code-tabs
 *   3. "Run a real scan, no signup" — pin to the Try-It-Now form
 *   4. "Apply for a key when ready" — pin to the apply CTA
 *
 * Once-per-visitor via sessionStorage. A11y: focus-trap on the step
 * card, ESC closes, Enter advances. Anchors found by their existing
 * data-testid so the tour stays decoupled from page DOM details.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, SkipForward, Lightbulb } from "lucide-react";


const SESSION_KEY = "qb_docs_tour_seen_v1";


const STEPS = [
  {
    id: "leftrail",
    targetTestId: "qorbom-docs-leftrail",
    title: "Navigate by section",
    body:
      "The left rail groups every page in the QorBOM Partner API docs. Click " +
      "any link to jump straight there — Quickstart, Authentication, Webhooks, " +
      "and more.",
    placement: "right",
    icon: Lightbulb,
  },
  {
    id: "code-samples",
    targetTestId: "qorbom-docs-quickstart-tabs",
    title: "Code samples in 4 languages",
    body:
      "Every quickstart shows the same flow in curl, Python, Node, and Go. " +
      "Hit the copy button on any block to paste it straight into your terminal.",
    placement: "bottom",
    icon: Lightbulb,
  },
  {
    id: "try-it-now",
    targetTestId: "qorbom-docs-tryit-form",
    title: "Run a real scan — no signup",
    body:
      "Paste any public GitHub URL and we'll run a live QorBOM scan against it. " +
      "Rate-limited to 1/IP/day so production partners aren't crowded out.",
    placement: "top",
    icon: Lightbulb,
  },
  {
    id: "apply",
    targetTestId: "qorbom-docs-quickstart-cta-apply",
    title: "Apply for a partner key",
    body:
      "Like what you see? The Apply form takes ~2 minutes. We mint a sandbox " +
      "key within two business days and you're off to the races.",
    placement: "bottom",
    icon: Lightbulb,
  },
];


export default function QorbomDocsTour() {
  const [stepIdx, setStepIdx] = useState(-1);   // -1 = not started
  const [rect,    setRect]    = useState(null); // viewport rect of target

  // Start once per session, after the page has had a moment to lay out.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.sessionStorage.getItem(SESSION_KEY) === "1") return undefined;
    const t = window.setTimeout(() => setStepIdx(0), 1100);
    return () => window.clearTimeout(t);
  }, []);

  // Track the target element on every step + on resize/scroll.
  useEffect(() => {
    if (stepIdx < 0 || stepIdx >= STEPS.length) {
      setRect(null);
      return undefined;
    }
    const step = STEPS[stepIdx];
    const update = () => {
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({
        top:    r.top,    left:  r.left,
        width:  r.width,  height: r.height,
        bottom: r.bottom, right: r.right,
      });
      // Scroll the target into view if it's far off-screen.
      const offV = r.top < 80 || r.bottom > window.innerHeight - 80;
      if (offV) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    // First paint may not have the element yet — retry briefly.
    update();
    const retry = window.setTimeout(update, 350);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      window.clearTimeout(retry);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, [stepIdx]);

  // Keyboard support
  useEffect(() => {
    if (stepIdx < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape")             { close(); }
      else if (e.key === "Enter" || e.key === "ArrowRight") { next(); }
      else if (e.key === "ArrowLeft")     { prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  const close = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) { /* ignore */ }
    setStepIdx(-1);
  };
  const next = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else close();
  };
  const prev = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  if (stepIdx < 0 || !rect) return null;

  const step    = STEPS[stepIdx];
  const isLast  = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;

  // Compute step-card placement so it doesn't fly off screen.
  const cardW = 360;
  const margin = 18;
  let cardTop, cardLeft;
  if (step.placement === "right") {
    cardTop  = rect.top;
    cardLeft = rect.right + margin;
  } else if (step.placement === "bottom") {
    cardTop  = rect.bottom + margin;
    cardLeft = Math.max(margin, rect.left);
  } else if (step.placement === "top") {
    cardTop  = rect.top - 220;
    cardLeft = Math.max(margin, rect.left);
  } else { /* left */
    cardTop  = rect.top;
    cardLeft = Math.max(margin, rect.left - cardW - margin);
  }
  if (cardLeft + cardW > window.innerWidth - margin) {
    cardLeft = window.innerWidth - cardW - margin;
  }
  if (cardTop < margin) cardTop = margin;
  if (cardTop + 240 > window.innerHeight - margin) {
    cardTop = window.innerHeight - 240 - margin;
  }

  const Icon = step.icon || Lightbulb;

  return (
    <div
      data-testid="qorbom-docs-tour-root"
      aria-modal="true"
      role="dialog"
      aria-labelledby="qorbom-docs-tour-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        pointerEvents: "none",   // backdrop ignores clicks; only card + scrim do
      }}
    >
      {/* Backdrop with a cutout around the target */}
      <SpotlightBackdrop rect={rect} onClick={close} />

      {/* Highlight ring around the target */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top:    rect.top    - 8,
          left:   rect.left   - 8,
          width:  rect.width  + 16,
          height: rect.height + 16,
          borderRadius: 12,
          border: "2px solid #00C896",
          boxShadow: "0 0 0 6px rgba(0,200,150,0.25)",
          pointerEvents: "none",
          transition: "all 240ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />

      {/* Step card */}
      <div
        data-testid={`qorbom-docs-tour-step-${step.id}`}
        style={{
          position: "fixed",
          top:  cardTop,
          left: cardLeft,
          width: cardW,
          maxWidth: "calc(100vw - 36px)",
          background: "#FFFFFF",
          color: "#0F172A",
          borderRadius: 8,
          border: "1px solid #E2E8F0",
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18)",
          padding: 20,
          pointerEvents: "auto",
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          transition: "all 240ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start",
                      justifyContent: "space-between", gap: 12,
                      marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8,
                        fontSize: 10, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.18em",
                        color: "#0033A0" }}>
            <Icon size={12} />
            Step {stepIdx + 1} of {STEPS.length}
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="qorbom-docs-tour-close"
            aria-label="Skip tour"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              padding: 4, color: "#94A3B8",
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Title */}
        <h3
          id="qorbom-docs-tour-title"
          style={{
            margin: 0, marginBottom: 8,
            fontSize: 17, fontWeight: 700, lineHeight: 1.3,
            color: "#0F172A",
            fontFamily: "Manrope, system-ui, sans-serif",
          }}
        >
          {step.title}
        </h3>

        {/* Body */}
        <p style={{ margin: 0, marginBottom: 16,
                    fontSize: 13, lineHeight: 1.5, color: "#475569" }}>
          {step.body}
        </p>

        {/* Footer — Skip / Back / Next */}
        <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", gap: 8 }}>
          <button
            type="button"
            onClick={close}
            data-testid="qorbom-docs-tour-skip"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              padding: 0, fontSize: 12, color: "#94A3B8",
              fontWeight: 500, display: "flex", alignItems: "center", gap: 4,
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            }}
          >
            <SkipForward size={11} /> Skip tour
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            {!isFirst && (
              <button
                type="button"
                onClick={prev}
                data-testid="qorbom-docs-tour-back"
                style={{
                  background: "#FFFFFF", border: "1px solid #CBD5E1",
                  color: "#475569", borderRadius: 4,
                  padding: "7px 12px", fontSize: 12, fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                }}
              >Back</button>
            )}
            {isLast ? (
              <Link
                to="/qorbom/become-a-partner"
                onClick={close}
                data-testid="qorbom-docs-tour-cta-apply"
                style={{
                  background: "#0033A0", color: "#FFFFFF",
                  borderRadius: 4, padding: "7px 14px",
                  fontSize: 12, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6,
                  textDecoration: "none",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                }}
              >
                Apply for a key <ArrowRight size={12} />
              </Link>
            ) : (
              <button
                type="button"
                onClick={next}
                data-testid="qorbom-docs-tour-next"
                style={{
                  background: "#0033A0", color: "#FFFFFF",
                  border: "none", borderRadius: 4,
                  padding: "7px 14px", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", display: "inline-flex",
                  alignItems: "center", gap: 6,
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                }}
              >
                Next <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Progress dots */}
        <div style={{ display: "flex", justifyContent: "center",
                      gap: 6, marginTop: 14 }}>
          {STEPS.map((_, i) => (
            <span key={i}
                  style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: i === stepIdx ? "#00C896" : "#E2E8F0",
                    transition: "background 200ms",
                  }} />
          ))}
        </div>
      </div>
    </div>
  );
}


// Backdrop with a soft cutout around the spotlighted target.
// SVG mask makes the rectangle "hole" without breaking pointer events.
function SpotlightBackdrop({ rect, onClick }) {
  return (
    <svg
      onClick={onClick}
      data-testid="qorbom-docs-tour-backdrop"
      style={{
        position: "fixed", inset: 0,
        width: "100vw", height: "100vh",
        pointerEvents: "auto",
        cursor: "pointer",
      }}
      aria-hidden="true"
    >
      <defs>
        <mask id="spot">
          <rect width="100%" height="100%" fill="white" />
          <rect
            x={rect.left - 6}    y={rect.top - 6}
            width={rect.width + 12} height={rect.height + 12}
            rx={10} ry={10}
            fill="black"
          />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.55)" mask="url(#spot)" />
    </svg>
  );
}
